import { AccordionDetails, Grid, TextField, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { AccountData, Language, StorefrontCustomInquiryData } from '../../../api';
import { Accordion, AccordionSummary } from '../../../calendar/dialogs/EditOrderDialog/components/Accordion';
import { StorefrontPanels } from '../StorefrontDrawer';
import { useAuth } from '../../../session/InternalAuthProvider';
import { FormRichTextField } from '../../../experienceSettings/formComponents/FormRichTextField';

type CustomInquiryPanelProps = {
  expanded?: StorefrontPanels;
  onPanelChange: (panel: StorefrontPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  panelName: StorefrontPanels;
};

type CustomInquiryFormValues = {
  custom_inquiry?: StorefrontCustomInquiryData;
};

export const CustomInquiryPanel = ({ expanded, onPanelChange, panelName }: CustomInquiryPanelProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<CustomInquiryFormValues>();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const languages = account.customer_facing_widgets_languages;

  const customInquiry = values.custom_inquiry || {};

  const handleFieldChange = (field: string, lang: string, value: string) => {
    void setFieldValue('custom_inquiry', {
      ...customInquiry,
      [field]: { ...(customInquiry as Record<string, Record<string, string>>)[field], [lang]: value },
    });
  };

  return (
    <Accordion expandedPanelName={expanded} panelName={panelName} onChange={onPanelChange}>
      <AccordionSummary title={t('storefronts.custom_inquiry')} />
      <AccordionDetails>
        <Grid container flexDirection="column" gap={3}>
          <Typography variant="body2" color="text.secondary">
            {t('storefronts.custom_inquiry_panel_description')}
          </Typography>

          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.custom_inquiry_title')}</Typography>
            {languages.map((lang) => (
              <TextField
                key={lang}
                label={t('storefronts.custom_inquiry_field_lang', {
                  field: t('storefronts.custom_inquiry_title'),
                  lang: t(`emailTemplates.${lang}`),
                })}
                value={(customInquiry as Record<string, Record<string, string>>).title?.[lang] || ''}
                onChange={(e) => handleFieldChange('title', lang, e.target.value)}
                dir={lang === Language.HE ? 'rtl' : 'ltr'}
                fullWidth
                size="small"
              />
            ))}
          </Grid>

          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.custom_inquiry_subtitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('storefronts.custom_inquiry_subtitle_description')}
            </Typography>
            <FormRichTextField labelFieldName="custom_inquiry.subtitle" disabled={false} />
          </Grid>

          <Grid container flexDirection="column" gap={1}>
            <Typography fontWeight={600}>{t('storefronts.custom_inquiry_button_label')}</Typography>
            {languages.map((lang) => (
              <TextField
                key={lang}
                label={t('storefronts.custom_inquiry_field_lang', {
                  field: t('storefronts.custom_inquiry_button_label'),
                  lang: t(`emailTemplates.${lang}`),
                })}
                value={(customInquiry as Record<string, Record<string, string>>).button_label?.[lang] || ''}
                onChange={(e) => handleFieldChange('button_label', lang, e.target.value)}
                dir={lang === Language.HE ? 'rtl' : 'ltr'}
                fullWidth
                size="small"
              />
            ))}
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
